import { useState, useCallback, useMemo } from "react";
import { useComments, useCreateComment, useLikeComment, useDeleteComment } from "@/hooks/useComments";
import { CommentItem } from "./CommentItem";
import { CommentInput } from "./CommentInput";
import { Spinner } from "@/components/ui/Spinner";
import type { Comment } from "@/lib/api";
import clsx from "clsx";

function collectAddresses(comments: Comment[]): string[] {
  const addresses = new Set<string>();
  for (const c of comments) {
    addresses.add(c.commenterAddress);
    for (const r of c.replies || []) {
      addresses.add(r.commenterAddress);
    }
  }
  return Array.from(addresses);
}

interface CommentsSectionProps {
  articleId: string;
  walletAddress?: string;
  canComment: boolean;
  isConnected: boolean;
  jwt?: string | null;
  isArticleWriter?: boolean;
}

export function CommentsSection({
  articleId,
  walletAddress,
  canComment,
  isConnected,
  jwt,
  isArticleWriter,
}: CommentsSectionProps) {
  const [sort, setSort] = useState<"newest" | "top">("newest");
  const [replyingTo, setReplyingTo] = useState<{ id: string; address: string } | null>(null);

  const { data, isLoading } = useComments(articleId, sort);
  const createComment = useCreateComment(articleId);
  const likeComment = useLikeComment(articleId);
  const deleteComment = useDeleteComment(articleId);

  const commenterAddresses = useMemo(
    () => collectAddresses(data?.comments || []),
    [data?.comments],
  );

  const handleSubmit = useCallback(
    (body: string) => {
      if (jwt && isArticleWriter) {
        // Writer commenting via JWT — backend resolves wallet from JWT identity.
        createComment.mutate(
          { commenterAddress: "", body, parentId: replyingTo?.id || undefined, jwt },
          { onSuccess: () => setReplyingTo(null) },
        );
      } else {
        if (!walletAddress) return;
        createComment.mutate(
          { commenterAddress: walletAddress, body, parentId: replyingTo?.id || undefined },
          { onSuccess: () => setReplyingTo(null) },
        );
      }
    },
    [walletAddress, createComment, replyingTo, jwt, isArticleWriter],
  );

  const handleDelete = useCallback(
    (commentId: string) => {
      if (jwt && isArticleWriter) {
        deleteComment.mutate({ commentId, commenterAddress: "", jwt });
      } else {
        if (!walletAddress) return;
        deleteComment.mutate({ commentId, commenterAddress: walletAddress });
      }
    },
    [walletAddress, deleteComment, jwt, isArticleWriter],
  );

  const handleLike = useCallback(
    (commentId: string) => {
      if (jwt && isArticleWriter) {
        likeComment.mutate({ commentId, likerAddress: "", jwt });
      } else {
        if (!walletAddress) return;
        likeComment.mutate({ commentId, likerAddress: walletAddress });
      }
    },
    [walletAddress, likeComment, jwt, isArticleWriter],
  );

  return (
    <section id="comments" className="mt-12 border-t border-beige/20 pt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold">
          Comments{data?.totalCount ? ` (${data.totalCount})` : ""}
        </h2>
        <div className="flex items-center gap-1 text-xs font-body">
          <button
            onClick={() => setSort("newest")}
            className={clsx(
              "px-2 py-1 rounded transition-colors",
              sort === "newest" ? "bg-black text-cream" : "text-grey hover:text-black",
            )}
          >
            Newest
          </button>
          <button
            onClick={() => setSort("top")}
            className={clsx(
              "px-2 py-1 rounded transition-colors",
              sort === "top" ? "bg-black text-cream" : "text-grey hover:text-black",
            )}
          >
            Top
          </button>
        </div>
      </div>

      <div>
        {isLoading && (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6" />
          </div>
        )}
        {!isLoading && (!data?.comments || data.comments.length === 0) && (
          <div className="py-10 text-center">
            <p className="text-sm text-grey font-body">No comments yet. Be the first!</p>
          </div>
        )}
        {data?.comments?.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            canInteract={canComment}
            onLike={handleLike}
            onReply={(id, address) => setReplyingTo({ id, address })}
            onDelete={handleDelete}
            walletAddress={walletAddress}
            writerAddress={data.writerAddress}
          />
        ))}
      </div>

      <CommentInput
        onSubmit={handleSubmit}
        loading={createComment.isPending}
        canComment={canComment}
        isConnected={isConnected}
        replyingToAddress={replyingTo?.address || null}
        onCancelReply={() => setReplyingTo(null)}
        commenterAddresses={commenterAddresses}
      />
    </section>
  );
}
